import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import api from "./../utils/api";
import { formatToLocalTime } from "../../src/utils/formatToLocalTime";

const UserProfile = () => {
  const [userData, setUserData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [editForm, setEditForm] = useState({ Name: "", Lastname: "", Email: "" });
  const [resetForm, setResetForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (username) {
      api
        .get(`/users/${username}`)
        .then((res) => {
          setUserData(res.data);
          setEditForm({
            Name: res.data.Name || "",
            Lastname: res.data.Lastname || "",
            Email: res.data.Email || "",
          });
        })
        .catch((error) => console.error("Failed to fetch user profile:", error));
    }
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/users/${userData.UserID}`, {
        ...editForm,
        Roles: userData.Roles?.map((r) => r.RoleID) || [],
      });
      setUserData({ ...userData, ...editForm });
      setEditOpen(false);
      showSnackbar("Profile updated successfully.");
    } catch (err) {
      console.error("Failed to update user:", err);
      showSnackbar("Error updating profile.", "error");
    }
  };

  if (!userData) return null;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: 1000,
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: 4,
          overflow: "hidden",
        }}
      >
        {/* Left Side */}
        <Box
          sx={{
            width: { xs: "100%", md: "30%" },
            backgroundColor: "#f5f5f5",
            p: 3,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Avatar sx={{ width: 100, height: 100, mb: 2 }}>
            {userData.Name?.charAt(0)}
          </Avatar>
          <Typography variant="h6">
            {userData.Name} {userData.Lastname}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userData.Roles?.map((r) => r.RoleName).join(", ") || "No Role"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {userData.Username}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              sx={{ mr: 1 }}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LockResetIcon />}
              onClick={() => setResetOpen(true)}
            >
              Reset Password
            </Button>
          </Box>
        </Box>

        {/* Right Side */}
        <Box sx={{ width: { xs: "100%", md: "70%" }, p: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Full Name
              </Typography>
              <Typography variant="body1">
                {userData.Name} {userData.Lastname}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{userData.Email}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Typography
                variant="body1"
                color={userData.Status === "active" ? "green" : "red"}
              >
                {userData.Status}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Roles
              </Typography>
              <Typography variant="body1">
                {userData.Roles?.map((r) => r.RoleName).join(", ") || "None"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Last Login
              </Typography>
              <Typography variant="body1">
                {userData.LastLogin
                  ? formatToLocalTime(userData.LastLogin)
                  : "Never"}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            fullWidth
            label="First Name"
            value={editForm.Name}
            onChange={(e) => setEditForm({ ...editForm, Name: e.target.value })}
          />
          <TextField
            margin="dense"
            fullWidth
            label="Last Name"
            value={editForm.Lastname}
            onChange={(e) => setEditForm({ ...editForm, Lastname: e.target.value })}
          />
          <TextField
            margin="dense"
            fullWidth
            label="Email"
            type="email"
            value={editForm.Email}
            onChange={(e) => setEditForm({ ...editForm, Email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetOpen}
        onClose={() => {
          setResetOpen(false);
          setResetForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
          setResetStep(1);
        }}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetStep === 1 ? (
            <TextField
              margin="dense"
              fullWidth
              label="Current Password"
              type="password"
              value={resetForm.currentPassword}
              onChange={(e) => setResetForm({ ...resetForm, currentPassword: e.target.value })}
            />
          ) : (
            <>
              <TextField
                margin="dense"
                fullWidth
                label="New Password"
                type="password"
                value={resetForm.newPassword}
                onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
              />
              <TextField
                margin="dense"
                fullWidth
                label="Confirm New Password"
                type="password"
                value={resetForm.confirmPassword}
                onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setResetOpen(false);
            setResetForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
            setResetStep(1);
          }}>
            Cancel
          </Button>
          {resetStep === 1 ? (
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await api.post("/verify-current-password", {
                    currentPassword: resetForm.currentPassword,
                  });
                  setResetStep(2);
                } catch (err) {
                  showSnackbar("Current password is incorrect.", "error");
                }
              }}
            >
              Verify
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={async () => {
                if (resetForm.newPassword !== resetForm.confirmPassword) {
                  showSnackbar("Passwords do not match.", "error");
                  return;
                }
                try {
                  await api.post("/reset-own-password", {
                    currentPassword: resetForm.currentPassword,
                    newPassword: resetForm.newPassword,
                  });
                  setResetOpen(false);
                  setResetForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                  setResetStep(1);
                  showSnackbar("Password updated successfully.");
                } catch (err) {
                  showSnackbar(err.response?.data?.message || "Password reset failed.", "error");
                }
              }}
              disabled={
                !resetForm.newPassword ||
                !resetForm.confirmPassword ||
                resetForm.newPassword !== resetForm.confirmPassword
              }
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
